import { routes } from '@spa/core/router'
import store from '@spa/redux/store'
import moment from 'moment'
import { matchRoutes } from 'react-router-dom'

import type { ParamsState } from '../../../reducers'
import { locationsSelectors } from '../../../selectors'

export const VIEWS = {
  MONTH: 'month',
  FLEXIBLE: 'flexible',
  LABELS: 'labels',
  DAY: 'day',
  DAYPRINT: 'day-print',
  WEEK: 'week',
  BULKACTIONS: 'bulk-actions',
  MONTH_ATTENDANCE_ABSENCE: 'month-attendance-absence'
}

/**
 * Check if [pathname] is different from planning v2 urls
 */
export const isLeavingPlannings = (pathname: string): boolean => {
  const matching = matchRoutes([{ path: routes.plannings.path }], pathname)

  return !matching?.length
}

/**
 * Check if [date] searchParams has changed
 */
export const isDifferentDate = (
  searchParams: ParamsState,
  newSearchParams: ParamsState
): boolean => {
  return searchParams.date !== newSearchParams.date
}

/**
 * Check if new [date] searchParams is in the previous timerange
 */
export const isDifferentWeek = (
  currentDate: string,
  newSearchParams: ParamsState
): boolean => {
  return !moment(newSearchParams.date).isBetween(
    moment(currentDate).startOf('isoWeek'),
    moment(currentDate).endOf('isoWeek'),
    'day',
    '[]'
  )
}

/**
 * Check if new and old team searchParams are from the same location
 */
export const isTeamCached = (
  searchParams: ParamsState,
  newSearchParams: ParamsState
): boolean => {
  const state = store.getState()
  const location = locationsSelectors.getCachedById(
    // @ts-ignore migration from js(x) to ts(x)
    state,
    newSearchParams.locationId
  )

  const fromTeamIdZero = searchParams.teamId === 0
  const isSameLocation = searchParams.locationId === newSearchParams.locationId
  const locationIncludeNewTeam = newSearchParams?.teamId
    ? location?.team_ids?.includes(newSearchParams.teamId)
    : false

  return fromTeamIdZero && isSameLocation && locationIncludeNewTeam
}

/**
 * Check if [team] searchParams has changed
 */
export const isDifferentTeam = (
  searchParams: ParamsState,
  newSearchParams: ParamsState
): boolean => {
  return searchParams.teamId !== newSearchParams.teamId
}

/**
 * Check if [team] searchParams has changed but data is not stale
 */
export const isDifferentTeamButCached = (
  searchParams: ParamsState,
  newSearchParams: ParamsState
): boolean => {
  return (
    isDifferentTeam(searchParams, newSearchParams) &&
    isTeamCached(searchParams, newSearchParams)
  )
}

/**
 * Check if [location] searchParams has changed
 */
export const isDifferentLocation = (
  searchParams: ParamsState,
  newSearchParams: ParamsState
): boolean => {
  return searchParams.locationId !== newSearchParams.locationId
}

/**
 * Returns true if the hydrated data will be considered stale
 */
export const shouldAbortStaleRequests = ({
  searchParams,
  newSearchParams
}: {
  searchParams: ParamsState
  newSearchParams: ParamsState
}): boolean => {
  return (
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    isDifferentWeek(searchParams.date, newSearchParams) ||
    isDifferentLocation(searchParams, newSearchParams) ||
    (isDifferentTeam(searchParams, newSearchParams) &&
      !isTeamCached(searchParams, newSearchParams))
  )
}
